import { useDisclosure } from '@mantine/hooks';
import { Modal, Button, MultiSelect, Stack, Loader } from '@mantine/core';
import { PROPOSALACTIONS } from '../../reducers/proposalReducer';
import { Proposal, OtherData } from '@/interfaces/types';
import { useTranslation } from 'react-i18next';
import { IconPlus, IconRotate2 } from '@tabler/icons-react';
import { useProposal, USERFEEDBACKACTIONS } from '../../context/ProposalContext';
import EditableTable from '@/components/Tables/EditableTable';
import { useGeneration } from '@/hooks/useGeneration';

export function GenerateLawyers() {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    proposal,
    dispatchProposal,
    userFeedback,
    dispatchUserFeedback,
    otherData,
    dispatchOtherData,
  } = useProposal();
  const { loadingLawyers } = userFeedback;
  const { expertise } = otherData;

  const { withService, cancelField } = useGeneration({
    dispatchProposal,
    dispatchUserFeedback,
    dispatchOtherData,
  });

  const handleGenerateLawyers = () => {
    const config = {
      input: proposal.input,
      documentLanguage: proposal.language,
      constraints: {
        requiredLawyers: proposal.constraints?.requiredLawyers || [],
        teamComposition: proposal.constraints?.teamComposition || [],
      },
      proposal,
    };

    withService(async (svc) => {
      await svc.generateLawyers(config, {
        dispatchProposal,
        dispatchUserFeedback,
        dispatchOtherData,
      });
    });
    close();
  };

  const handleCancel = () => {
    cancelField('lawyers');
    // Just in case
    dispatchUserFeedback({
      type: USERFEEDBACKACTIONS.SET_FIELD,
      field: 'loadingLawyers',
      value: false,
    });
  };

  return (
    <>
      <Modal opened={opened} onClose={close} title={t('generateFurtherLawyers')}>
        <Stack>
          <MultiSelect
            label={t('requiredLawyers')}
            data={otherData.allLawyers.map((lawyer: any) => lawyer.LawyerName)}
            value={proposal.constraints?.requiredLawyers || []}
            onChange={(value) => {
              dispatchProposal({
                type: PROPOSALACTIONS.SET_CONSTRAINTS,
                field: 'requiredLawyers',
                value: value,
              });
            }}
            placeholder={t('selectRequiredLawyers')}
            searchable
            hidePickedOptions
          />
          <EditableTable
            rows={proposal.constraints?.teamComposition || []}
            columns={[
              {
                title: t('expertise'),
                key: 'team',
                type: 'select',
                selectValues: expertise,
                exclusiveSelect: true,
              },
              {
                title: t('quantity'),
                key: 'quantity',
                type: 'number',
                width: '5.5rem',
              },
            ]}
            dispatchData={dispatchProposal}
            dispatchAddAction={PROPOSALACTIONS.ADD_TEAM_CONSTRAINT}
            dispatchRemoveAction={PROPOSALACTIONS.REMOVE_TEAM_CONSTRAINT}
          />
          <Button variant="default" onClick={close}>
            {t('cancel')}
          </Button>
          <Button
            color="cyan"
            variant="light"
            leftSection={<IconRotate2 size={16} />}
            onClick={handleGenerateLawyers}
          >
            {t('generate')}
          </Button>
        </Stack>
      </Modal>

      <Button
        color={loadingLawyers ? 'orange' : 'cyan'}
        variant="light"
        leftSection={loadingLawyers ? <Loader size="xs" color="cyan" /> : <IconPlus size={18} />}
        style={{ marginRight: '0.5rem' }}
        onClick={() => {
          if (loadingLawyers) {
            handleCancel();
          } else {
            open();
          }
        }}
      >
        {loadingLawyers ? t('cancel') : t('generateLawyers')}
      </Button>
    </>
  );
}
